/** Пометки у ячеек строки, нормализованной на выходе (`raw.normalized`, бэкенд
 *  `volume_norm.rs`): число из ячейки, которую разбор не прочитал, наименование
 *  по тексту листа вместо голой марки. В базе строка прежняя — пометка говорит,
 *  что изменено и откуда. Модуль — без импортов значений (тест — `node --test`). */
import type { PtoVolumeNormalized } from '@shared/api';

/** Ячейки строки, у которых бывает пометка. */
export type NormalizedField = 'name' | 'code' | 'unit' | 'quantity';

/** Поля строки, которые показывает ячейка: графа «Шифр» — шифр и обозначение
 *  (`CodeCell`). */
const SHOWN: Record<NormalizedField, readonly string[]> = {
  name: ['name'],
  code: ['code', 'designation'],
  unit: ['unit'],
  quantity: ['quantity'],
};

/** Отметка нормализации из `raw` позиции; нет или не та форма — null. */
export function normalizedOf(raw: unknown): PtoVolumeNormalized | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const n = (raw as Record<string, unknown>).normalized;
  if (n === null || typeof n !== 'object' || Array.isArray(n)) return null;
  const v = n as Record<string, unknown>;
  if (typeof v.rule !== 'string' || typeof v.note !== 'string') return null;
  const was = v.was !== null && typeof v.was === 'object' ? (v.was as Record<string, unknown>) : {};
  const by = v.by !== null && typeof v.by === 'object' ? (v.by as Record<string, unknown>) : null;
  return {
    rule: v.rule,
    note: v.note,
    by:
      by && typeof by.id === 'string' && typeof by.row === 'string'
        ? { id: by.id, row: by.row }
        : undefined,
    was,
    unit_from: v.unit_from === 'cell' || v.unit_from === 'ai' ? v.unit_from : undefined,
  };
}

/** Прежнее значение поля строкой: «—» вместо пустого. */
function wasText(v: unknown): string {
  return v === null || v === undefined || v === '' ? '—' : String(v);
}

/** Пометка у ячейки `field`: короткий текст и подсказка (полное пояснение);
 *  поле не менялось — null. */
export function normalizedHint(
  raw: unknown,
  field: NormalizedField,
): { text: string; title: string } | null {
  const n = normalizedOf(raw);
  if (!n || !SHOWN[field].some((k) => k in n.was)) return null;
  const by = n.by?.row ?? 'текст листа';
  const title = n.note;
  switch (field) {
    case 'quantity':
      return { text: `из ячейки, подтверждено: ${by}`, title };
    case 'unit':
      return { text: n.unit_from === 'cell' ? 'из ячейки' : `по ${by}`, title };
    case 'name':
      return n.rule === 'mark_name'
        ? { text: `по тексту листа (${by}); в РД — «${wasText(n.was.name)}»`, title }
        : { text: `в РД: «${wasText(n.was.name)}»`, title };
    case 'code':
      return { text: 'марка из наименования РД', title };
  }
}
